import hintImage from "@assets/contact-hint.png"
import { ArrowLink } from "@features/atoms/ArrowLink"
import { MouseFollower } from "@features/atoms/MouseFollower"
import { GlowingBox } from "@features/gfx/GlowingBox"
import { useAppState } from "@hooks/useAppState"
import { cn } from "@utils/tw"
import { defineBlock, EditableText } from "eddev/blocks"
import { useAppData } from "eddev/hooks"
import { Link, useRoute } from "eddev/routing"
import { LayoutGroup, motion } from "motion/react"
import { useEffect, useId, useState } from "react"
import { useWindowSize } from "react-use"

export const meta: BlockMeta = {
  title: "Site Footer",
  lazyLoad: false,
}

export default defineBlock("parts/part-footer", (props) => {
  const route = useRoute()
  const { width } = useWindowSize()
  const isMobile = width < 900

  // Main + social menus
  const mainMenu = useAppData((data) => data.mainMenu?.menuItems?.nodes)
  const socialMenu = useAppData((data) => data.socialLinks?.menuItems?.nodes)
  const legalLinks: { url: string; label: string }[] = [{ url: "/privacy-policy/", label: "Privacy Policy" }]

  // Enable/disable mouse following when hovering
  const [mouseFollowEnabled, setMouseFollowEnabled] = useState(false)

  // Tell the rest of the app when the footer is visible
  const appState = useAppState()
  useEffect(() => {
    return () => appState.setFooterVisible(false)
  }, [])

  // Hide the 'Contact' section on the contact and about page
  const includeContactLink = !route.pathname.includes("contact") && !route.pathname.includes("about")

  return (
    <motion.div
      data-footer-wrapper
      className="w-grid mx-auto py-grid-margin flex flex-col gap-grid-margin"
      onViewportEnter={() => {
        appState.setFooterVisible(true)
      }}
      onViewportLeave={() => {
        appState.setFooterVisible(false)
      }}
    >
      {/* Contact */}
      {includeContactLink && (
        <motion.div
          initial={{ opacity: 0, filter: "blur(20px)" }}
          whileInView={{ opacity: 1, filter: "none" }}
          exit={{ opacity: 0, filter: "blur(20px)" }}
          onMouseEnter={() => setMouseFollowEnabled(true)}
          onMouseLeave={() => setMouseFollowEnabled(false)}
        >
          <MouseFollower
            enabled={!isMobile && mouseFollowEnabled}
            render={(side) => {
              return <ContactHint side={side} />
            }}
          />
          <Link
            href="/contact/"
            className="py-9 border border-white/20 transition-colors hover:border-white/25 hover:bg-white/5 active:border-white/30 active:bg-white/10 active:transition-none no-underline flex flex-col items-center justify-center rounded-lg text-center"
          >
            <div className="type-label-l text-white/60 mb-4">
              <ArrowLink type="right">
                <EditableText as="div" store="contactLabel" defaultValue="Contact" plainText disableLineBreaks />
              </ArrowLink>
            </div>
            <EditableText
              as="div"
              store="contactText"
              defaultValue="Tailored solutions start here"
              className="type-title-m md:max-w-[20em] text-balance"
              plainText
              disableLineBreaks
            />
            <div className="relative w-full h-8 mt-8 md:hidden flex items-center justify-center">
              <ContactHint side="left" imgClassName="!relative !left-0" labelClassName="!relative !left-0" />
            </div>
          </Link>
        </motion.div>
      )}

      {/* Main footer */}
      <motion.div
        initial={{ opacity: 0, filter: "blur(20px)" }}
        whileInView={{ opacity: 1, filter: "none" }}
        exit={{ opacity: 0, filter: "blur(20px)" }}
      >
        <GlowingBox
          getParams={({ hovering, inView, mobile }) => ({
            opacity: inView ? (hovering ? 0.9 : 0.8) : 0.2,
            feather: 0,
          })}
          alpha={false}
          className="pt-7 transition-colors rounded-lg isolate overflow-hidden bg-black/10"
        >
          {/* First row */}
          <div className="grid-auto">
            {/* Footer title */}
            <div className="col-span-12 sm:col-span-6 md:col-span-9 pl-7">
              <EditableText
                store="footerTitle"
                as="div"
                className="type-title-l w-[6em] text-balance"
                defaultValue="Making the impossible, possible."
              />
            </div>

            {/* Main menu links */}
            <div className="col-span-12 sm:col-span-6 md:col-span-3 max-sm:pl-7 max-sm:pt-12">
              <ul className="type-label-l group/links flex flex-col">
                {mainMenu?.map((item, i) => (
                  <li key={i} className="flex items-start">
                    <Link
                      href={item.uri}
                      target={item.target}
                      className="no-underline py-2 block group-has-[a:hover]/links:opacity-60 transition-opacity hover:!opacity-100"
                    >
                      <ArrowLink type="right">{item.label}</ArrowLink>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Second row */}
          <div className="grid-auto mt-10 pb-6 items-end">
            {/* Email link */}
            <div className="col-span-12 sm:col-span-6 md:col-span-9 pl-7 max-sm:pb-4">
              <EditableText
                as="div"
                store="contact"
                placeholder="Add an email link"
                className="type-label-l simple-links"
              />
            </div>

            {/* Socials/Copyright */}
            <div className="col-span-12 sm:col-span-6 md:col-span-3 max-sm:pl-7 flex justify-between items-end pr-7">
              {/* Links */}
              <ul className="type-label-s group/links gap-x-4 flex md:flex-col">
                {socialMenu?.map((item, i) => (
                  <li key={i} className="flex items-start">
                    <Link
                      href={item.uri}
                      target={item.target}
                      className="no-underline block sm:pt-3 group-has-[a:hover]/links:opacity-60 transition-opacity hover:!opacity-100"
                    >
                      <ArrowLink type="external">{item.label}</ArrowLink>
                    </Link>
                  </li>
                ))}
              </ul>
              {/* Copyright */}
              <div className="type-label-s flex flex-col">
                <div className="relative text-right sm:text-left">
                  <div className="absolute pb-3 sm:static right-0 bottom-0">
                    <ul className="type-label-s flex flex-col">
                      {legalLinks?.map((item, i) => (
                        <li key={i} className="flex items-start">
                          <Link
                            href={item.url}
                            className="no-underline py-2 block text-white/60 hover:text-white transition-colors"
                          >
                            <ArrowLink type="right">{item.label}</ArrowLink>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <div>Copyright &copy; {new Date().getFullYear()}</div>
              </div>
            </div>
          </div>
        </GlowingBox>
      </motion.div>
    </motion.div>
  )
})

export function ContactHint(props: { side: "left" | "right"; imgClassName?: string; labelClassName?: string }) {
  const img = (
    <motion.div
      key="img"
      layout
      className={cn("absolute z-10 size-8", props.side === "left" ? "left-5" : "right-5", props.imgClassName)}
    >
      <img src={hintImage} className="size-8" />
    </motion.div>
  )
  const label = (
    <motion.div
      key="label"
      animate={props.side}
      layout
      className={cn(
        "absolute bg-white/90 text-black type-label-l px-4 h-8 flex items-center justify-center rounded-md text-nowrap",
        props.side === "left" ? "left-9 ml-2" : "right-9 mr-2",
        props.labelClassName,
      )}
    >
      Speak to our Experts
    </motion.div>
  )
  return (
    <LayoutGroup id={useId()}>
      {img}
      {label}
    </LayoutGroup>
  )
}
